import type * as amplitude from "@amplitude/analytics-browser";

import type { UnknownRecord } from "@/utils/type";

import type { LicenseInfo, UserInfo, UserPropertyValue } from "../types";

let instance: typeof amplitude | undefined;
let debug = false;

interface AmplitudeConfig {
  apiKey: string;
  reverseProxy: string;
  build: string;
  debug: boolean;
}

export async function initAmplitude(config: AmplitudeConfig, license: LicenseInfo, user: UserInfo) {
  instance = await import("@amplitude/analytics-browser");
  debug = config.debug;
  instance.init(config.apiKey, {
    autocapture: false,
    serverZone: "EU",
    serverUrl: config.reverseProxy || undefined,
    trackingOptions: { ipAddress: false },
  });
  identifyUser(config.build, license, user);
}

function identifyUser(build: string, license: LicenseInfo, user: UserInfo) {
  if (instance) {
    instance.setUserId(user.id);

    const screenSize = [
      screen.width * window.devicePixelRatio,
      screen.height * window.devicePixelRatio,
    ];
    const windowSize = [
      Math.round((100 * window.outerWidth) / screen.width),
      Math.round((100 * window.outerHeight) / screen.height),
    ];

    setAmplitudeUserProperties([
      ["company", user.company],
      ["role", user.role],
      ["type", license.type],
      ["isLicenseExpired", license.expired],
      ["plan", license.plan],
      ["build_hash", build],
      ["teams", user.teams.map((team) => team.id)],
      ["language", user.preferredLanguage],
      ["screenSize", `${screenSize[0]} x ${screenSize[1]}`],
      ["windowSize", `${windowSize[0]}% x ${windowSize[1]}%`],
    ]);
  }
}

/**
 * Set any custom amplitude user property
 * (the new value will be added to all future events)
 */
export function setAmplitudeUserProperties(entries: Array<[string, UserPropertyValue]>) {
  if (instance) {
    const identifyEvent = new instance.Identify();
    for (const [property, value] of entries) {
      identifyEvent.set(property, value);
    }
    instance.identify(identifyEvent);
  }
}

export function trackAmplitude(event: string, data: UnknownRecord) {
  if (instance) {
    if (debug) {
      // eslint-disable-next-line no-console
      console.info("trackEvent", event, data);
    }
    instance.track(event, data);
  }
}

export function resetAmplitude() {
  // Events get their user id when the queue is processed, not when tracked.
  // Flushing processes the queue while the user id is still set, before reset clears it.
  // Otherwise, users count doubly as company/role/type/etc = (none) in amplitude.
  instance?.flush();
  instance?.reset();
  instance = undefined;
}
